/* =========================================================
   MV — Magnify
   Desktop hover/click magnify only
   - Disabled on mobile
   - Prevents mobile PDP image disappearing after zoom/close
   - Avoids global overlay leakage
   - Cleans up image opacity/spinner state
   ========================================================= */

(() => {
  const IMAGE_SEL = ".image-magnify-hover";
  const OVERLAY_CLASS = "image-magnify-full-size";
  const DESKTOP_MQ = window.matchMedia("(min-width: 750px)");

  function getSpinner(image) {
    return image?.parentElement?.parentElement?.querySelector(".loading__spinner");
  }

  function showSpinner(image) {
    const spinner = getSpinner(image);
    if (spinner) spinner.classList.remove("hidden");
  }

  function hideSpinner(image) {
    const spinner = getSpinner(image);
    if (spinner) spinner.classList.add("hidden");
  }

  function resetImageState(image) {
    if (!image) return;

    image.style.removeProperty("opacity");
    image.style.removeProperty("visibility");
    image.style.removeProperty("transform");
  }

  function removeExistingOverlay(image) {
    const parent = image?.parentElement;
    if (!parent) return;

    parent.querySelectorAll(`.${OVERLAY_CLASS}`).forEach((overlay) => {
      overlay.remove();
    });

    resetImageState(image);
    hideSpinner(image);
  }

  function prepareOverlay(container, image) {
    container.className = OVERLAY_CLASS;
    container.setAttribute("aria-hidden", "true");
    container.style.backgroundImage = `url("${image.src}")`;
    container.style.backgroundColor = "var(--gradient-background)";
  }

  function createOverlay(image) {
    removeExistingOverlay(image);

    const overlayImage = document.createElement("img");
    overlayImage.src = image.src;

    const overlay = document.createElement("div");
    prepareOverlay(overlay, overlayImage);

    image.style.opacity = "0.5";
    showSpinner(image);

    overlayImage.onload = () => {
      hideSpinner(image);

      if (!image.parentElement) return;

      image.parentElement.insertBefore(overlay, image);
      image.style.opacity = "1";
    };

    overlayImage.onerror = () => {
      hideSpinner(image);
      resetImageState(image);
      overlay.remove();
    };

    return overlay;
  }

  function moveWithHover(image, overlay, event, zoomRatio) {
    if (!image || !overlay || !DESKTOP_MQ.matches) return;

    const ratio = image.height / image.width;
    const container = event.target.getBoundingClientRect();

    const xPosition = event.clientX - container.left;
    const yPosition = event.clientY - container.top;

    const xPercent = `${xPosition / (image.clientWidth / 100)}%`;
    const yPercent = `${yPosition / ((image.clientWidth * ratio) / 100)}%`;

    overlay.style.backgroundPosition = `${xPercent} ${yPercent}`;
    overlay.style.backgroundSize = `${image.width * zoomRatio}px`;
  }

  function magnify(image, event, zoomRatio) {
    if (!DESKTOP_MQ.matches) {
      removeExistingOverlay(image);
      return;
    }

    const overlay = createOverlay(image);

    overlay.onclick = () => {
      overlay.remove();
      resetImageState(image);
      hideSpinner(image);
    };

    overlay.onmousemove = (mousemoveEvent) => {
      moveWithHover(image, overlay, mousemoveEvent, zoomRatio);
    };

    overlay.onmouseleave = () => {
      overlay.remove();
      resetImageState(image);
      hideSpinner(image);
    };

    moveWithHover(image, overlay, event, zoomRatio);
  }

  function enableZoomOnHover(zoomRatio) {
    const images = document.querySelectorAll(IMAGE_SEL);

    images.forEach((image) => {
      removeExistingOverlay(image);

      image.onclick = null;

      if (!DESKTOP_MQ.matches) {
        resetImageState(image);
        hideSpinner(image);
        return;
      }

      image.onclick = (event) => {
        magnify(image, event, zoomRatio);
      };
    });
  }

  function cleanupMobileState() {
    if (DESKTOP_MQ.matches) return;

    document.querySelectorAll(IMAGE_SEL).forEach((image) => {
      removeExistingOverlay(image);
      image.onclick = null;
    });

    document.querySelectorAll(`.${OVERLAY_CLASS}`).forEach((overlay) => {
      overlay.remove();
    });
  }

  enableZoomOnHover(2);
  cleanupMobileState();

  DESKTOP_MQ.addEventListener("change", () => {
    enableZoomOnHover(2);
    cleanupMobileState();
  });

  document.addEventListener("shopify:section:load", () => {
    enableZoomOnHover(2);
    cleanupMobileState();
  });
})();